import 'preact/debug' // Enable for debugging
// Preact, hooks, and Redux
import {h, Fragment} from 'preact'
import {useEffect, useRef, useState} from 'preact/hooks'
import {useDispatch, useSelector} from 'react-redux'
import {unwrapResult} from '@reduxjs/toolkit'
import {Suspense, lazy} from 'preact/compat'
// Helmet for setting/changing the head metadata
import {Helmet} from 'react-helmet'
// Bootstrap components used for UI
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Image from 'react-bootstrap/Image'
// Redux thunks used for API calls
import fetchOrder from './redux/thunks/fetchOrder'
import fetchCart from './redux/thunks/fetchCart'
import fetchCustomerInfo from './redux/thunks/fetchCustomerInfo'
import fetchShippingProfileRequirement from './redux/thunks/fetchShippingProfileRequirement'
import fetchShippingProfiles from './redux/thunks/fetchShippingProfiles'
import fetchPayment from './redux/thunks/fetchPayment'
import processPostCartUpsell from './redux/thunks/processPostCartUpsell'
import updatePostCartUpsellPrice from './redux/thunks/updatePostCartUpsellPrice'
import fetchTransaction from './redux/thunks/fetchTransaction'
import validateDiscount from './redux/thunks/validateDiscount'
import addOrder from './redux/thunks/addOrder'
import processOrder from './redux/thunks/processOrder'
import processPaypalOrder from './redux/thunks/processPaypalOrder'
import generateTestInfo from './redux/thunks/generateTestInfo'
import updateInCartUpsellsPrice from './redux/thunks/updateInCartUpsellsPrice'
import removeOrderOffer from './redux/thunks/removeOrderOffer'
import setPaypalCheckout from './redux/thunks/setPaypalCheckout'
import getPaypalCheckout from './redux/thunks/getPaypalCheckout'
// Redux reducers and data selectors
import {bulkChangeOrder, changeOrder} from './redux/slices/orderSlice'
import {changePostCartUpsellsStatus} from './redux/slices/postCartUpsellsSlice'
import {changeShippingProfilesStatus, setShippingProfileDescription} from './redux/slices/shippingProfilesSlice'
import {changeCartStatus} from './redux/slices/cartSlice'
// import {changeInCartUpsellsStatus} from './redux/slices/inCartUpsellsSlice'
import {changeCustomerInfoStatus, paypalCustomerInfo} from './redux/slices/customerInfoSlice'
import {changeTransactionStatus} from './redux/slices/transactionSlice'
import {bulkChangePayment, changePayment, changePaymentStatus} from './redux/slices/paymentSlice'
// Basic components
import Loader from './components/loader'
import GrowlNotification from './components/growl-notification'
// GTM
import TagManager from 'react-gtm-module'

// Leaving these here for debugging purposes because Suspense and lazy make life hard
// import OnePage from './components/onepage/onepage'
// import TwoPage from './components/twopage/twopage'

// Spinner on load
const preloadingSpinner = document.getElementById('preloading-spinner')

// Templated Checkout
let Template

// Checkout is where all the logic for the checkout occurs.
// For example, ensuring calls are made in correct order.
const Checkout = ({headers, cart_token, order_id, paypal_order_id, ba_token, shop, gateway_response_gateway_id, cancel}) => {
  // Checkout container for fading in and loading state variable
  const checkoutContainer = useRef(null)

  // Error handling
  // ATTENTION: In the console.error, check the details for the actual line of the error
  const [error, setError] = useState(null)
  const [errorDuration, setErrorDuration] = useState(3000)
  const errorHandler = (error) => {
    setError(null)

    setTimeout(() => {
      if(error.message === 'Internal server error') {
        setError(<div dangerouslySetInnerHTML={{__html:error.data}} />)
        setErrorDuration(0)
      } else {
        setError(error.message)
        setErrorDuration(3000)
      }
    }, 1)
  }

  // Redux dispatch
  const dispatch = useDispatch()

  // Order data, which is used to for doAdd
  const order = useSelector(state => state.order.data)
  const orderStatus = useSelector(state => state.order.status)

  // Cart status and data
  const cartStatus = useSelector(state => state.cart.status)
  const cart = useSelector(state => state.cart.data)
  const {upsellToUpdate, orderOfferIDToRemove, offerKeyToRemove} = useSelector(state => state.cart)

  // Customer information used to create and edit orders (shipping and billing addresses)
  const customerInfo = useSelector(state => state.customerInfo.data)
  const customerInfoStatus = useSelector(state => state.customerInfo.status)

  // Payment data is actually data corresponding to a created order with an order ID, sent to doProcess
  const payment = useSelector(state => state.payment.data)
  const paymentStatus = useSelector(state => state.payment.status)
  
  // Shipping Profiles for knowing when to fetch whether shipping profiles are required and if so, fetching profiles themselves
  const {shippingProfiles} = useSelector(state => state.shippingProfiles.data)
  const shippingProfilesStatus = useSelector(state => state.shippingProfiles.status)

  // Discount code data, status, and error
  const {discountCode} = useSelector(state => state.discounts)
  const discounts = useSelector(state => state.discounts.data)
  const discountsStatus = useSelector(state => state.discounts.status)

  // Upsells data and status
  const postCartUpsells = useSelector(state => state.postCartUpsells.data)
  const postCartUpsellsStatus = useSelector(state => state.postCartUpsells.status)
  const {upsellKey} = useSelector(state => state.postCartUpsells)

  // Transaction status for successful or unsuccessful order processing
  const transactionStatus = useSelector(state => state.transaction.status)

  // DISPLAY LOADER AND REVEAL CONTENT
  const [loading, setLoading] = useState(false)

  // Navigation for one-page templates
  const [activeKey, setActiveKey] = useState(null)
  const [advance, setAdvance] = useState(true)

  // Allow for user to go back to section 1, update state to allow it
  const navigateToCustomerInfo = () => {
    dispatch(changeCustomerInfoStatus(''))

    // TODO don't need to remove discount_code if navigating to customer info
    // ... or do we?
    // if(discount_code) {
    //   dispatch(removeDiscount(discount_code))
    // }
    
    dispatch(changeCartStatus('idle'))

    setAdvance(true)
    setActiveKey('1')
    dispatch(changeOrder({
      property: 'route_override',
      value: '/'
    }))
  }

  const reveal = () => {
    if(preloadingSpinner.classList.contains('is-visible')) {
      preloadingSpinner.classList.remove('is-visible')
            
      setTimeout(() => {
        preloadingSpinner.style.display = 'none'
        checkoutContainer.current.classList.add('is-visible')
      }, 300)
    }

    setLoading(false)
  }
  
  const handleTransaction = () => {
    if(order.campaign_storefront_url) {
      dispatch(changeTransactionStatus('loading'))
      const campaignStorefrontUrl = order.campaign_storefront_url + (order.campaign_storefront_url.slice(-1) === '/' ? '' : '/')
      window.location = `${campaignStorefrontUrl}checkout/thank-you?order_id=${order.order_id}&cart_token=${order.cart_token}`
    } else {
      reveal()
    }
  }

  // const detectGiftOrder = ({offers}) => {
  //   const detection = offers.reduce((containsOnlyGifts, offer) => {
  //     if(containsOnlyGifts && offer.is_gift === 1) {
  //       return true
  //     } else {
  //       return false
  //     }
  //   }, true)


  //   dispatch(
  //     changeOrder({
  //       property: 'only_gifts',
  //       value: detection
  //     })
  //   )

  //   return detection
  // }

  const [gtmInitialized, setGtmInitialized] = useState(false)
  const [sublyticsTracking, setSublyticsTracking] = useState(null)

  // FETCH ORDER
  // Fetch or prepare data for doAdd
  useEffect(() => {
    if(orderStatus === 'idle') {
      const orderToFetch = {order_id, cart_token, headers}

      if(order.order_id && order.order_id !== '') {
        orderToFetch.order_id = order.order_id
      }

      if(order.offers) {
        orderToFetch.offers = order.offers
      }

      if(order.gift_cards) {
        orderToFetch.gift_cards = order.gift_cards
      }

      if(order.shipping_profile_id) {
        orderToFetch.shipping_profile_id = order.shipping_profile_id
      }

      if(order.route_override) {
        orderToFetch.route_override = order.route_override
      }

      // Fetch the order with data selectively synchronized with the current state
      dispatch(fetchOrder(orderToFetch))
        .then(unwrapResult)
        .then(response => {
          if(!gtmInitialized && response.campaign_checkout_google_analytics) {
            TagManager.initialize({
              gtmId: response.campaign_checkout_google_analytics
            })

            setGtmInitialized(true)
          }

          if(response.sublytics_tracking_connection_api_key_1 && response.sublytics_tracking_connection_api_key_2 && response.company_key) {
            setSublyticsTracking(`(function(key,objname,api,reqtype,debug){window["sa_analytics"]=objname;window[objname]=window[objname]||function(){
                (window[objname].tracker=window[objname].tracker||[]).push(arguments)};window[objname].start=(new Date).getTime();window[objname].key=key;window[objname].reqtype=reqtype;
                window[objname].debug=debug;window[objname].api=api;var script=document.createElement("script");var scripts=document.getElementsByTagName("script")[0];script.async=1;
                script.src="https://${response.sublytics_tracking_connection_api_key_1}/script/${response.company_key}/${response.sublytics_tracking_connection_api_key_2}";scripts.parentNode.insertBefore(script,scripts)})
                ("${response.sublytics_tracking_connection_api_key_2}","saa","https://${response.sublytics_tracking_connection_api_key_1}/hit/${response.company_key}/${response.sublytics_tracking_connection_api_key_2}","pixel",false);
                saa("page","view");`) 
          }
          
          Template = lazy(() => import(`./components/${response.checkout_type}/${response.checkout_type}`))
          
          // Order ID presence means we can fetch customer information or go to page 1
          if(response.order_id) {
            if(customerInfoStatus !== 'succeeded' && customerInfoStatus !== 'valid-email') {
              dispatch(changeCustomerInfoStatus('idle'))
            }
          } else {
            setActiveKey('1')
            reveal()
          }

          dispatch(changeCartStatus('idle'))
        })
        .catch(errorHandler)
    }
  }, [orderStatus, order, customerInfoStatus, gtmInitialized])

  const detectFreeOrder = ({total, recurring}) => {
    if(!recurring && total === 0) {
      if(order.payment_method_id !== '0') {
        dispatch(changeOrder({property: 'payment_method_id', value: '0'}))
      }
      if(payment.payment_method_id !== '0') {
        dispatch(changePayment({property: 'payment_method_id', value: '0'}))
      }
    } else {
      if(order.payment_method_id === '0') {
        dispatch(changeOrder({property: 'payment_method_id', value: '1'}))
      }
      if(payment.payment_method_id === '0') {
        dispatch(changePayment({property: 'payment_method_id', value: '1'}))
      }
    }
  }

  // FETCH CART
  useEffect(() => {
    if(cartStatus === 'idle') {
      // Add some data to calculate proper taxes
      const orderForCart = {
        ...order,
        offers: order.offers ? order.offers : cart.cart_data,
        ship_country: order.customers_address_shipping_id ? null : customerInfo.ship_country,
        ship_state: order.customers_address_shipping_id ? null : customerInfo.ship_state,
        ship_address1: order.customers_address_shipping_id ? null : customerInfo.ship_address1,
        ship_address2: order.customers_address_shipping_id ? null : customerInfo.ship_address2,
        ship_city: order.customers_address_shipping_id ? null : customerInfo.ship_city,
        ship_zipcode: order.customers_address_shipping_id ? null : customerInfo.ship_zipcode,
        bill_country: order.customers_address_billing_id ? null : customerInfo.bill_country,
        bill_state: order.customers_address_billing_id ? null : customerInfo.bill_state,
        bill_address1: order.customers_address_billing_id ? null : customerInfo.bill_address1,
        bill_address2: order.customers_address_billing_id ? null : customerInfo.bill_address2,
        bill_city: order.customers_address_billing_id ? null : customerInfo.bill_city,
        bill_zipcode: order.customers_address_billing_id ? null : customerInfo.bill_zipcode,
      }

      dispatch(fetchCart({order: orderForCart, headers}))
        .then(unwrapResult)
        .then(detectFreeOrder)
        .catch(errorHandler)
    } else if(cartStatus === 'update') {
      dispatch(updateInCartUpsellsPrice({
        campaign_id: order.campaign_id,
        offer_id: upsellToUpdate.upsell_offer_id,
        order_offer_quantity: upsellToUpdate.order_offer_quantity,
        discount_code: discounts.discounts ? discounts.discounts.discount_code : null,
        order_offer_item_options: upsellToUpdate.order_offer_item_options ? upsellToUpdate.order_offer_item_options : null,
        headers
      }))
        .then(unwrapResult)
        .then(detectFreeOrder)
        .catch(errorHandler)
    } else if(cartStatus === 'remove') {
      dispatch(removeOrderOffer({
        order_offer_id: orderOfferIDToRemove,
        cart_token: order.cart_token,
        key: offerKeyToRemove,
        headers
      }))
        .then(unwrapResult)
        .then(response => {
          if(!cart.cart_data.length && response.redirect) {
            window.location = response.redirect
          }

          detectFreeOrder(response)
        })
        .catch(errorHandler)
    }
  }, [cartStatus, order, upsellToUpdate, discounts])

  // FETCH SHIPPING PROFILES
  useEffect(() => {
    if(
      order &&
      (
        (order.route_override && order.route_override !== '/') ||
        (!order.route_override && order.route !== '/') ||
        (order.checkout_type === 'onepage')
      )
    ) {
      if(shippingProfilesStatus === 'idle') {
        dispatch(fetchShippingProfileRequirement({
          campaign_id: order.campaign_id,
          ship_country: order.shipping_information_required ? customerInfo.ship_country : customerInfo.bill_country,
          customers_address_shipping_id: order.shipping_information_required ? order.customers_address_shipping_id : order.customers_address_billing_id,
          offers: order.offers ? order.offers : cart.cart_data,
          headers
        }))
          .then(unwrapResult)
          .catch(errorHandler)
      } else if(shippingProfilesStatus === 'required') {
        dispatch(fetchShippingProfiles({
          campaign_id: order.campaign_id,
          ship_country: order.shipping_information_required ? customerInfo.ship_country : customerInfo.bill_country,
          ship_state: customerInfo.ship_state,
          customers_address_shipping_id: order.shipping_information_required ? order.customers_address_shipping_id : order.customers_address_billing_id,
          offers: order.offers ? order.offers : cart.cart_data,
          headers
        }))
          .then(unwrapResult)
          .then(response => {
            if(response.shipping_profiles.length === 1) {
              dispatch(changeOrder({
                property: 'shipping_profile_id',
                value: response.shipping_profiles[0].value
              }))
            }
          })
          .catch(errorHandler)
      } else if(shippingProfilesStatus === 'succeeded' && (order.shipping_profile_id && order.shipping_profile_id !== '0')) {
        const selectedShippingProfile = shippingProfiles.find(({value}) => value === order.shipping_profile_id)

        if(selectedShippingProfile) {
          dispatch(setShippingProfileDescription(selectedShippingProfile.description))
        }
      }
    }
  }, [shippingProfilesStatus, shippingProfiles, order, cart])

  // FETCH CUSTOMER INFO
  useEffect(() => {
    if(customerInfoStatus === 'idle') {
      dispatch(fetchCustomerInfo({order_id: order.order_id, headers}))
        .then(unwrapResult)
        .then((response) => {
          dispatch(changePaymentStatus('idle'))
          
          // If there's no shipping profile, we need to send customer's address
          // for shipping profiles and taxes
          if(!order.customers_address_shipping_id) {
            const {ship_country, ship_state, ship_zipcode} = response

            dispatch(bulkChangeOrder([{
              property: 'ship_country',
              value: ship_country
            }, {
              property: 'ship_state',
              value: ship_state,
            }, {
              property: 'ship_zipcode',
              value: ship_zipcode
            }]))

            if(!order.shipping_information_required && order.customers_address_billing_id) {
              setActiveKey('3')
            } else {
              setActiveKey('2')
            }
          } else {
            setActiveKey('3')
          }
        })
        .catch(errorHandler)
    } else if(customerInfoStatus === 'valid' || customerInfoStatus === 'valid-email') {
      if(order.route_override) {
        dispatch(changeOrder({property: 'route_override', value: undefined}))
      }

      dispatch(changePaymentStatus('add-offers'))
    } else if(customerInfoStatus === 'test') {
      dispatch(generateTestInfo())
        .then(unwrapResult)
        .then(() => {
          dispatch(changeShippingProfilesStatus('idle'))
        })
        .catch(errorHandler)
    }
  }, [customerInfoStatus, order])

  // ADD AN ORDER
  useEffect(() => {
    if(paymentStatus === 'add' || paymentStatus === 'add-offers') { // doAdd
      setLoading(true)

      dispatch(changeCustomerInfoStatus(''))
      
      dispatch(addOrder({payment, order: {...order, offers: paymentStatus === 'add-offers' ? cart.cart_data : order.offers}, customerInfo, headers}))
        .then(unwrapResult)
        .then(response => {
          reveal()

          if(!response.customers_address_shipping_id && order.shipping_information_required) {
            setActiveKey('2')
          } else {
            if(payment.payment_method_id === '0') {
              dispatch(changeTransactionStatus('add'))
            } else {
              setActiveKey(String(Number(activeKey) + 1))
            }
          }
        })
        .catch(errorHandler)
    } else if(paymentStatus === 'idle') { // view order
      dispatch(fetchPayment({payment, order, customerInfo, headers}))
        .then(unwrapResult)
        .then(() => {
          if(paypal_order_id && cancel !== '1') {
            dispatch(bulkChangePayment([{
              property: 'payment_method_id',
              value: '6'
            }, {
              property: 'transaction_token',
              value: ba_token ? `${paypal_order_id}_${ba_token}` : paypal_order_id
            }]))

            dispatch(changeTransactionStatus('add-paypal'))
          } else if(order.route === '/transaction') {
            dispatch(changeTransactionStatus('idle'))
          } else if(order.route === '/upsells') {
            dispatch(changeTransactionStatus('add'))
          } else {
            reveal()
          }
        })
        .catch(errorHandler)
    } else if(paymentStatus === 'valid') { // doProcess
      dispatch(changeTransactionStatus('add'))
    } else if(paymentStatus === 'process') { // doAdd and then doProcess
      setLoading(true)

      let addOrderData = {payment, order, headers}

      if(!order.shipping_information_required) {
        const {
          bill_fname,
          bill_lname,
          bill_organization,
          bill_country,
          bill_state,
          bill_address1,
          bill_address2,
          bill_city,
          bill_zipcode,
          email,
          phone,
          shipping_same
        } = customerInfo

        addOrderData = {
          ...addOrderData,
          customerInfo: {
            bill_fname,
            bill_lname,
            bill_organization,
            bill_country,
            bill_state,
            bill_address1,
            bill_address2,
            bill_city,
            bill_zipcode,
            email,
            phone,
            shipping_same
          }
        }
      } else {
        addOrderData = {...addOrderData, customerInfo}
      }

      dispatch(addOrder(addOrderData))
        .then(unwrapResult)
        .then(() => {
          dispatch(changeTransactionStatus('add'))
        })
        .catch(errorHandler)
    }
  }, [paymentStatus, payment, order, customerInfo])

  // FETCH TRANSACTION
  useEffect(() => {
    if(transactionStatus === 'idle') { // view_transaction
      dispatch(fetchTransaction({order_id: order.order_id, headers}))
        .then(unwrapResult)
        .then(handleTransaction)
        .catch(errorHandler)
    } else if(transactionStatus === 'add') { // process_order
      setLoading(true)

      let processOrderData = {payment, offers: order.offers ? order.offers : cart.cart_data, headers}

      if(!order.shipping_information_required) {
        const {
          bill_fname,
          bill_lname,
          bill_organization,
          bill_country,
          bill_state,
          bill_address1,
          bill_address2,
          bill_city,
          bill_zipcode,
          email,
          phone,
          shipping_same
        } = customerInfo

        processOrderData = {
          ...processOrderData,
          customerInfo: {
            bill_fname,
            bill_lname,
            bill_organization,
            bill_country,
            bill_state,
            bill_address1,
            bill_address2,
            bill_city,
            bill_zipcode,
            email,
            phone,
            shipping_same
          }
        }
      }

      dispatch(processOrder(processOrderData))
        .then(unwrapResult)
        .then(response => {
          if(response.data.upsells) {
            dispatch(changePostCartUpsellsStatus('idle'))
          } else if(response.data.transaction.post_data) {
            window.location = `${response.data.transaction.post_data}&gateway_response_gateway_id=${response.data.transaction.gateway_response_gateway_id}`
          } else {
            handleTransaction()
          }
        })
        .catch(error => {
          if(error.message === 'ERROR: Shipping Method Required') {
            dispatch(changeShippingProfilesStatus('idle'))
          }
          errorHandler(error)
        })
    } else if(transactionStatus === 'add-paypal') {
      let transaction_token;

      if (gateway_response_gateway_id) {
        transaction_token = gateway_response_gateway_id;
      } else if (paypal_order_id) {
        if (ba_token) {
          transaction_token = `${paypal_order_id}_${ba_token}`;
        } else {
          transaction_token = paypal_order_id;
        }
      }

      dispatch(
        processPaypalOrder({ transaction_token, order_id: order.order_id, headers })
      )
        .then(unwrapResult)
        .then((response) => {
          if (response.data.upsells) {
            dispatch(changePostCartUpsellsStatus('idle'));
          } else if (response.data.transaction.post_data) {
            window.location = `${response.data.transaction.post_data}&gateway_response_gateway_id=${response.data.transaction.gateway_response_gateway_id}`;
          } else {
            handleTransaction();
          }
        })
        .catch((error) => {
          if(error.message === 'ERROR: Order is already complete.') {
            dispatch(changeTransactionStatus('idle'))
          } else {
            errorHandler(error)
          }
        });
    }
  }, [transactionStatus, payment, customerInfo, order, cart.cart_data])

  // VALIDATE DISCOUNTS
  useEffect(() => {
    if(discountsStatus === 'idle') {
      const discountToValidate = order.order_id ? {
        discount_code: discountCode,
        customer_email: customerInfo.email,
        order,
        headers
      } : {
        discount_code: discountCode,
        customer_email: customerInfo.email,
        offers: cart.cart_data,
        connection_id: order.connection_id,
        headers
      }

      dispatch(validateDiscount(discountToValidate))
        .then(unwrapResult)
        .catch(errorHandler)
    }
  }, [discountsStatus, discountCode, order, cart, customerInfo])

  // PROCESS POST CART UPSELLS
  useEffect(() => {
    if(postCartUpsellsStatus === 'idle') {
      setLoading(true)
      dispatch(updatePostCartUpsellPrice({
        campaign_id: order.campaign_id,
        offer_id: postCartUpsells[0].offer_id,
        order_offer_quantity: postCartUpsells[0].order_offer_quantity,
        discount_code: discounts.discounts ? discounts.discounts.discount_code : null,
        order_offer_item_options: postCartUpsells[0].order_offer_item_options ? postCartUpsells[0].order_offer_item_options : null,
        headers
      }))
        .then(unwrapResult)
        .then(() => {
          reveal()

          setActiveKey('4')
        })
        .catch(errorHandler)
    } else if(postCartUpsellsStatus === 'process' || postCartUpsellsStatus === 'skip') {
      setLoading(true)

      dispatch(processPostCartUpsell({
        order_id: order.order_id,
        offers: [{
          order_offer_quantity: postCartUpsells[0].order_offer_quantity,
          offer_id: postCartUpsells[0].offer_id,
          order_offer_upsell: 1,
          parent_order_offer_id: postCartUpsells[0].parent_order_offer_id,
          order_offer_upsell_weight: upsellKey,
          discount_code: order.discount_code ? order.discount_code : null,
          status_type_id: postCartUpsellsStatus === 'process' ? 4 : 7,
          order_offer_item_options: postCartUpsells[0].order_offer_item_options && postCartUpsells[0].order_offer_item_options
        }],
        final_upsell: postCartUpsells.length === 1,
        headers}))
        .then(unwrapResult)
        .then(() => {
          if(postCartUpsells.length - 1 > 0) {
            dispatch(changePostCartUpsellsStatus('idle'))
          } else {
            handleTransaction()
          }
        })
        .catch(errorHandler)
    }
  }, [postCartUpsellsStatus, order, postCartUpsells, upsellKey])

  const createOrder = () => {
    return dispatch(setPaypalCheckout({
      merchant_id: order.merchants.find(({payment_method_id}) => payment_method_id == 6).merchant_id,
      data: cart.express_checkout.paypal,
      order,
      headers
    }))
      .then(response => {
        dispatch(changePayment({property: 'payment_method_id', value: 6}))
        return response.payload
      })
  }

  const onApprove = (data) => {
    setLoading(true)

    const previousPhone = customerInfo.phone

    return dispatch(getPaypalCheckout({
      merchant_id: order.merchants.find(({payment_method_id}) => payment_method_id == 6).merchant_id,
      data,
      headers
    }))
      .then((response) => {
        dispatch(bulkChangeOrder([{
          property: 'payment_method_id',
          value: 6
        }, {
          property: 'payment_token',
          value: JSON.stringify({orderID: data.orderID})
        }, {
          property: 'merchant_id',
          value: order.merchants.find(({payment_method_id}) => payment_method_id == 6).merchant_id
        }]))

        dispatch(paypalCustomerInfo({...response.payload.data, phone: previousPhone}))

        dispatch(changePayment({property: 'payment_token', value: JSON.stringify({orderID: data.orderID})}))
        dispatch(changePayment({property: 'transaction_token', value: data.orderID}))

        reveal()
      })
      .catch(errorHandler)
  }

  return (
    <Fragment>
      <Helmet>
        <title>{orderStatus === 'succeeded' && order ? order.campaign_checkout_title : 'Secure Checkout'}</title>
        <script type="text/javascript">{sublyticsTracking}</script>
      </Helmet>

      {(order && order.checkout_type === 'twopage') && <Loader isVisible={loading} />}

      {error && <GrowlNotification variant="danger" duration={errorDuration}>An error occurred: {error}</GrowlNotification>}
      
      <div
        ref={checkoutContainer}
        className={`checkout-container ${order.checkout_type === 'onepage' && order.checkout_template === 'template2' ? 'shaker-spoon' : ''}`}
      >
        <Suspense fallback={<Loader isVisible={true} />}>
          <div className="header">
            <Container
              className={`${order.checkout_type === 'onepage' && order.checkout_template === 'template2' ? 'mb-0' : ''}`}
              fluid={order.checkout_type === 'onepage' && order.checkout_template !== 'template2'}
            >
              <Row style={{marginBottom: 0}}>
                <Col
                  xs={12}
                  md={7}
                  className={`${order.checkout_type === 'onepage' && order.checkout_template === 'template2' ? 'py-1' : 'py-3 pl-md-5'} ${order.checkout_template !== 'template3' && order.checkout_type === 'twopage' && "text-center"}`}>
                  <Row>
                    <Col
                      md={12}
                      lg={11}
                      xl={10}
                      className={`${order.checkout_type === 'onepage' && order.checkout_template === 'template1' ? 'mr-0 ml-auto' : ''}`}
                    >
                      {order && <Image src={order.checkout_logo} style={{maxHeight: '3rem'}}/>}
                    </Col>
                  </Row>
                </Col>

                <Col xs={0} md={5} className={`pr-md-5 ${order.checkout_type === 'onepage' && order.checkout_template === 'template1' ? 'bg-light border-left' : ''}`}/>
              </Row>
            </Container>
          </div>

          {order.checkout_type === 'onepage' && order.checkout_template === 'template2' && <Container>
            <Row>
              <Col>
                <h2 class="secure-checkout text-right h5 mb-0 mr-3">
                  🔒 Secure Checkout
                </h2>
              </Col>
            </Row>
          </Container>}

          <Container
            fluid={order.checkout_type === 'onepage' && order.checkout_template === 'template1'}
            className="d-flex flex-column"
            style={{flex: '1 0 auto'}}
          >
            <Row className="mb-0" style={{flex: '1 0 auto'}}>
              {Template && <Template
                createOrder={createOrder}
                cartToken={cart_token}
                orderId={order_id}
                shop={shop}
                onApprove={onApprove}
                errorHandler={errorHandler}
                activeKey={activeKey}
                setActiveKey={setActiveKey}
                advance={advance}
                setAdvance={setAdvance}
                navigateToCustomerInfo={navigateToCustomerInfo}
              />}
              {/* <OnePage createOrder={createOrder} cartToken={cart_token} orderId={order_id} shop={shop} onApprove={onApprove} errorHandler={errorHandler} /> */}
              {/* <TwoPage createOrder={createOrder} cartToken={cart_token} orderId={order_id} shop={shop} onApprove={onApprove} errorHandler={errorHandler} checkoutTemplate={order.checkout_template} /> */}
            </Row>
          </Container>
        </Suspense>

        <Container fluid as={'footer'} className="mt-3 mt-md-1">
          <Row className="mb-0 p-1 align-items-end">
            <Col md={3} className="d-none d-md-block">
              Copyright &copy; {new Date().getFullYear()} Shaker &amp; Spoon
            </Col>

            <Col className="text-center we-accept">
              We Accept Visa Mastercard American Express Discover
            </Col>

            <Col md={3} className="text-right d-none d-md-block">
              <a><strong className="text-uppercase">Terms &amp; Conditions</strong></a>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  )
}

export default Checkout


// WEBPACK FOOTER //
// ./client/resources/js/checkout/Checkout.js